<template>
    <div>
        <v-overlay
            opacity="0.99"
            v-if="initializing"
            :absolute="true"
            :value="1"
        >
            <div class="text-center">
                <p class="text-center">
                    Please wait while the book is being loaded...
                </p>
                <v-progress-circular
                    indeterminate
                    color="primary"
                    class="text-center"
                    :size="50"
                ></v-progress-circular>
            </div>
        </v-overlay>

        <div
            id="viewer"
            class="spreads"
            :style="{
                width: '100%',
                'min-width': '800px',
                height: '90vh',
                display: currentBook ? 'block' : 'none',
            }"
        ></div>
        <v-container id="btn-holder" v-bind:class="currentBook ? 'active' : ''">
            <v-row>
                <v-btn @click="goBack" elevation="0" id="prev">
                    <v-icon>mdi-arrow-left</v-icon>
                </v-btn>
                <v-spacer></v-spacer>
                <v-btn @click="goForward" elevation="0" id="next">
                    <v-icon>mdi-arrow-right</v-icon>
                </v-btn>
            </v-row>
        </v-container>
        <div
            id="page-location"
            style="width: 100%; position: absolute; bottom: -15px"
        >
            <p class="text-center font-weight-bold pt-3">
                <template v-if="chapterNumber > 0">Chapter {{ chapterNumber }}</template>
                <template v-else>Table of Contents</template>
                / Page
                {{ currentPageObj.chapterPage }}
            </p>
        </div>
    </div>
</template>

<script>
import {mapState, mapMutations, mapActions, mapGetters} from "vuex";
import ePub, { EpubCFI } from "epubjs";
import { mapFields } from "vuex-map-fields";

export default {
    name: "ViewBook",
    data() {
        return {
            book: false,
            containerHeight: 0,
            initializing: true,
        };
    },
    props: ["checkOnline"],
    computed: {
        ...mapFields([
            "loading",
            "toc",
            "currentPageObj",
            "currentHighlights",
            "hasLoaded",
        ]),
        ...mapFields({
            stateRendition: "rendition",
        }),
        ...mapState([
            "ebooks",
            "ebookStates",
            "currentBook",
            "currentBookmarks",
            "isSidebarActive",
            "currentPage",
            "autoLoadedBook",
        ]),
        ...mapGetters(["chapterNumber"]),
        bookId() {
            return this.$route.params.id;
        },
        canAutoload() {
            return this.autoLoadedBook;
        },
        parsedEBooks() {
            return this.ebooks.map((book) => {
                return {
                    ...book,
                    cover_image_url: book.product.cover_image_id
                        ? `${process.env.VUE_APP_API_URL}files/o-files/view-file/${book.product.cover_image_id}`
                        : null,
                };
            });
        },
    },
    watch: {
        canAutoload(newCount) {
            if (newCount) {
                this.loadEbook(this.currentBook);
                this.$router.push(`/`);
            }
        },
        "$route.query.page": {
            handler(newPageCfi) {
                if (newPageCfi && this.stateRendition) {
                    this.stateRendition.display(newPageCfi);
                }
            },
            immediate: true,
        },
    },
    methods: {
        ...mapActions(["setHighlightState", "setCurrentPageState"]),
        ...mapMutations(["setBookState"]),
        goBack() {
            this.stateRendition.prev();
        },
        goForward() {
            this.stateRendition.next();
        },
        keyListener(e) {
            if ((e.keyCode || e.which) === 37) {
                this.goBack();
            }
            if ((e.keyCode || e.which) === 39) {
                this.goForward();
            }
        },
        loadEbook(bookId) {
            // Clear current book DOM
            document.getElementById("viewer").innerHTML = "";

            // Initialize ePub
            const book = ePub(
                process.env.VUE_APP_API_URL +
                    "ebooks/ebooks/" +
                    bookId +
                    ".epub"
            );

            // Set the book state, this will load annotations/bookmarks from localStorage
            this.setBookState(bookId);

            // Initialize rendition
            const rendition = book.renderTo("viewer", {
                width: "100%",
                height: "100%",
                ignoreClass: "annotator-hl",
                allowScriptedContent: true,
            });

            // Set the rendition in the state
            this.stateRendition = rendition;

            // Display the current page, or the first page if no page is set
            rendition.display(this.currentPageObj?.pagePath || 0);

            // Initialize existing highlights
            this.currentHighlights.map((item) => {
                rendition.annotations.highlight(item.id, {}, (e) => {
                    console.log("highlight clicked", e.target);
                });
            });

            // Wait for navigation to be loaded
            book.loaded.navigation.then((navigation) => {
                this.toc = [...navigation.toc];

                this.hasLoaded = true;

                this.initListeners(book, rendition);
            });

            // When book is ready, loop bookmarks
            book.ready.then(async () => {
                const savedPageStage = { ...this.currentPageObj };
                this.initializing = true;
                for (let item of this.currentBookmarks) {
                    await rendition.display(item.pagePath);
                    item.chapterPage =
                        book.rendition.currentLocation().start.displayed.page;
                }
                this.initializing = false;
                await rendition.display(savedPageStage?.pagePath || 0);
            });
        },
        searchChapter(q, book, chapterIndex) {
            let item = book.spine.get(chapterIndex);
            return item
                .load(book.load.bind(book))
                .then(item.find.bind(item, q))
                .finally(item.unload.bind(item));
        },
        initListeners(book, rendition) {
            rendition.on("selected", (cfiRange, contents) => {
                // Get range
                book.getRange(cfiRange).then((range) => {
                    if (range) {
                        const text = range.toString();

                        const highlight = {
                            id: cfiRange,
                            text: text,
                            canDelete: false,
                        };

                        const existingHighlight = this.currentHighlights.find(
                            (highlight) => highlight.id === cfiRange
                        );

                        if (!existingHighlight) {
                            // Highlight the selection
                            rendition.annotations.highlight(
                                cfiRange,
                                {},
                                (e) => {
                                    console.log("highlight clicked", e.target);
                                }
                            );

                            this.setHighlightState(highlight);
                        } else {
                            this.$store.commit("setHighlightState", {
                                ...highlight,
                                canDelete: true,
                            });

                            const range = new EpubCFI(cfiRange);
                            rendition.annotations.remove(range, "highlight");
                        }

                        // Clear the selection
                        contents.window.getSelection().removeAllRanges();
                    }
                });
            });

            rendition.on("keyup", this.keyListener);

            rendition.on("relocated", (location) => {
                if (this.initializing) {
                    return false;
                }

                if (location.atStart) {
                    this.currentPageObj = {
                        chapter: 1,
                        chapterPage: 1,
                        pagePath: 0,
                    };
                    this.setCurrentPageState(this.currentPageObj);
                } else {
                    this.currentPageObj = {
                        chapter: location.start.index + 1,
                        chapterPage: location.start.displayed.page,
                        pagePath: location.start.cfi,
                    };
                    this.setCurrentPageState(this.currentPageObj);
                }

                const newPageCfi = location.start.cfi;
                const currentQueryPage = this.$route.query.page ?? "";

                if (newPageCfi !== currentQueryPage) {
                    this.$router.push({
                        name: "viewEBook",
                        params: { id: this.bookId },
                        query: { page: location.start.cfi },
                    });
                }
            });

            // Handle internal links
            const $this = this;
            rendition.hooks.content.register(function (contents) {
                let doc = contents.document;
                let links = doc.querySelectorAll("internallink");

                links.forEach(function (link) {
                    // Set link styles
                    link.style.color = "blue";
                    link.style.textDecoration = "underline";
                    link.style.cursor = "pointer";

                    // Add event listener
                    link.addEventListener("click", async function () {
                        // Get the href from the link
                        const href = link.getAttribute("internallinkid");

                        // Check if href contains chapter
                        if (href.includes("chapter:")) {
                            const chapterIndex = href.split(":")[1];

                            const subchapterTitle = href.split(":")[2];

                            // Check if subchapter title exists
                            if (!subchapterTitle) {
                                // If no subchapter exists, we display the chapter
                                await rendition.display(chapterIndex);

                                return false;
                            }

                            // Decode base64
                            const subchapterTitleReadable =
                                atob(subchapterTitle);

                            const chapterLocations = await $this.searchChapter(
                                subchapterTitleReadable,
                                book,
                                chapterIndex
                            );

                            const chapterLocation = chapterLocations.find(
                                (loc) => loc.excerpt === subchapterTitleReadable
                            );

                            if (chapterLocation) {
                                rendition.display(chapterLocation.cfi);
                            } else {
                                rendition.display(chapterIndex);
                            }
                        }
                    });
                });
            });
        },
    },
    mounted() {
        this.loadEbook(this.bookId);
    },
};
</script>
